import { Box, Pressable } from "../../../../../mads";
import { Heading } from "../../../../../mads/Heading";
import { Close } from "../../../../../mads/icons";

import { colors } from "@indeed/ifl-tokens";
import React, { useEffect, useState } from "react";
import {
  Platform,
  Animated,
  StyleSheet,
  Easing,
  type ViewStyle,
  KeyboardAvoidingView,
} from "react-native";

function useAnimatedNative(): boolean {
  if (Platform.OS === "android" && Platform.Version === 31) {
    return false;
  }
  return true;
}

export interface SlideInProps {
  children?: React.ReactNode;
  labelClose?: string;
  title?: React.ReactNode;
  isOpen: boolean;
  sx?: ViewStyle;
  onDismiss: () => void;
}

/**
 * SlideIn component - A bottom sheet modal that slides in from the bottom.
 * Migrated from IFL to viewjob since it's only used in this directory.
 *
 * @deprecated This component is deprecated. Please use React Navigation's Stack.Screen component.
 * See the App.tsx component for usage examples.
 *
 */
export function SlideIn(props: SlideInProps): React.JSX.Element {
  const { children, title, isOpen, sx, onDismiss, labelClose } = props;
  // Set a very large initial offset so it doesn't appear on first render
  const [offsetY, setOffsetY] = useState(9999);

  const [translate] = useState(new Animated.Value(offsetY));
  const [opacity] = useState(new Animated.Value(0));
  const useNativeDriver: boolean = useAnimatedNative();

  useEffect(() => {
    const animation = Animated.timing(translate, {
      toValue: isOpen ? 0 : offsetY,
      duration: 150,
      useNativeDriver,
      easing: Easing.inOut(Easing.ease),
    });
    const opacityAnimation = Animated.timing(opacity, {
      toValue: isOpen ? 0.5 : 0,
      duration: 150,
      useNativeDriver,
      easing: Easing.inOut(Easing.ease),
    });

    animation.start();
    opacityAnimation.start();

    return () => {
      animation.stop();
      opacityAnimation.stop();
    };
  }, [isOpen, offsetY, translate, opacity, useNativeDriver]);

  return (
    <Animated.View
      style={[styles.root, !isOpen && styles.disablePointerEvent, !isOpen && styles.closed]}
    >
      <Pressable
        onPress={onDismiss}
        sx={{ height: "100%", width: "100%" }}
        accessibilityLabel={labelClose || "Close"}
        accessibilityRole="button"
        accessibilityElementsHidden={!isOpen}
        importantForAccessibility={isOpen ? "yes" : "no-hide-descendants"}
      >
        <Animated.View style={[styles.backdrop, { opacity }]} />
      </Pressable>
      <KeyboardAvoidingView behavior={"padding"}>
        <Animated.View
          accessibilityViewIsModal={isOpen}
          onLayout={(e) => {
            setOffsetY(e.nativeEvent.layout.height);
          }}
          style={[
            {
              flexGrow: 0,
              flexShrink: 0,
              transform: [{ translateY: translate }],
            },
          ]}
        >
          <Box sx={[sx]}>
            <Box sx={styles.heading}>
              <Heading sx={styles.title}>{title}</Heading>
              <Pressable onPress={onDismiss} accessibilityRole="button">
                <Close color={colors.foreground["[base]"]} />
              </Pressable>
            </Box>
            {children}
          </Box>
        </Animated.View>
      </KeyboardAvoidingView>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: "flex-end",
  },
  disablePointerEvent: {
    pointerEvents: "none",
  },
  backdrop: {
    backgroundColor: "rgb(0, 0, 0)",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heading: {
    alignItems: "center",
    borderBottomColor: colors.neutral["400"],
    borderBottomWidth: 1,
    flexDirection: "row",
    flexBasis: "auto",
    flexGrow: 0,
    flexShrink: 0,
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
  },
  title: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: "auto",
  },
  closed: {
    display: "none",
  },
});
